import { useEffect, useRef } from "react";

import { InfoIcon } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Button from "components/Button";
import { FormField } from "components/Form";
import ImageCreator from "components/ImageCreator";
import { SelectComponent } from "components/Select";

import { useCompanySettingsDialog } from "./useCompanySettingsDialog";

export default function TemplateSelectionStep() {
  const { t } = useTranslation();
  const stampInputRef = useRef<HTMLInputElement>(null);

  const { watch, control, setValue } = useFormContext();

  const templateId = watch("templateId");
  const languageId = watch("languageId");

  const { getTemplatesQuery, useTemplatePreview, getDefaultTemplateQuery } = useCompanySettingsDialog();

  const { data: templatePreview, refetch: refetchTemplatePreview } = useTemplatePreview(templateId, languageId);

  const templates =
    getTemplatesQuery.data?.resultList
      .filter((template: any) => template.id !== 100)
      .map((template: any) => ({
        label: template.name,
        value: template.id.toString(),
      })) || [];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    //if (file) addStampMutation.mutate({ file });

    // Add uploaded file to the form state
    if (file) {
      setValue("templateFile", file);
    }
  };

  const handleDownload = async () => {
    const response = await getDefaultTemplateQuery.refetch();
    if (response.data) {
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template.docx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }
  };

  useEffect(() => {
    if (templateId !== "100") {
      refetchTemplatePreview();
    }
  }, [templateId, languageId, refetchTemplatePreview]);

  return (
    <div className="space-y-4">
      <FormField
        control={control}
        name="templateId"
        render={({ field }) => (
          <SelectComponent
            field={field}
            label={t("settings.companySettingsDialog.findingsTemplate")}
            options={templates}
          />
        )}
      />

      {templateId !== "100" ? (
        <div className="h-[350px] bg-general-gray-200 flex items-center justify-center">
          <ImageCreator
            data={templatePreview}
            alt="Doc preview"
            className="h-full object-contain bg-general-gray-300"
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <input
            ref={stampInputRef}
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            onChange={handleChange}
          />
          <Button type="button" className="w-full" onClick={() => stampInputRef.current?.click()} variant="dotted">
            {t("button.upload")}
          </Button>
          <div className="flex border border-general-green-700 bg-general-mint-600 rounded-md p-4 gap-2.5">
            <div>
              <InfoIcon className="stroke-general-green-700" />
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-sm font-semibold">Primjer nalaza</span>
              <span className="text-general-green-800 text-sm">
                Primjerak popunjenog uzorka, koji možete prilagoditi svojim potrebama.{" "}
                <span onClick={handleDownload} className="underline cursor-pointer font-semibold">
                  Preuzmi primjer
                </span>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
